/**
 * Optimized FinalCTA Component
 * Performance improvements:
 * - React.memo for preventing unnecessary re-renders
 * - useMemo for memoization
 * - useCallback for event handlers
 * - Intersection Observer for view tracking
 * - Analytics tracking for views and CTA clicks
 */

'use client';

import { memo, useCallback, useRef, useEffect } from 'react';
import Link from "next/link";
import { siteSettings } from "@workspace/utils";
import { useIntersectionObserver, useAnalytics } from '@/hooks/usePerformance';

interface FinalCTAProps {
    headline?: string;
    subheading?: string;
    primaryCTA?: {
        text: string;
        href: string;
    };
    secondaryCTA?: {
        text: string;
        href: string;
    };
    className?: string;
}

function FinalCTAComponent({
    headline = `Ready to Transform Your Admissions Journey?`,
    subheading = `Join hundreds of successful applicants who got into Harvard, Stanford, INSEAD, and other top schools with ${siteSettings.siteName}'s ethical, expert-led consulting.`,
    primaryCTA = {
        text: "Pricing",
        href: "/pricing?course=MIM&schools=4"
    },
    secondaryCTA = {
        text: "Free Profile Review",
        href: "/profile-evaluation"
    },
    className = ""
}: FinalCTAProps) {
    const sectionRef = useRef<HTMLDivElement>(null);
    const hasTrackedView = useRef(false);
    const { trackEvent } = useAnalytics();

    // Track section visibility
    const isInView = useIntersectionObserver(sectionRef, {
        threshold: 0.3,
        rootMargin: '50px',
    });

    // Track when section becomes visible (only once)
    useEffect(() => {
        if (isInView && !hasTrackedView.current) {
            trackEvent('final_cta_section_view', {
                section_headline: headline,
                primary_cta_text: primaryCTA.text,
                secondary_cta_text: secondaryCTA.text,
            });
            hasTrackedView.current = true;
        }
    }, [isInView, headline, primaryCTA.text, secondaryCTA.text, trackEvent]);

    // Track primary CTA clicks
    const handlePrimaryCTAClick = useCallback(() => {
        trackEvent('final_cta_primary_clicked', {
            cta_text: primaryCTA.text,
            cta_destination: primaryCTA.href,
            section: 'final_cta',
            cta_type: 'primary',
        });
    }, [primaryCTA.text, primaryCTA.href, trackEvent]);

    // Track secondary CTA clicks
    const handleSecondaryCTAClick = useCallback(() => {
        trackEvent('final_cta_secondary_clicked', {
            cta_text: secondaryCTA.text,
            cta_destination: secondaryCTA.href,
            section: 'final_cta',
            cta_type: 'secondary',
        });
    }, [secondaryCTA.text, secondaryCTA.href, trackEvent]);

    return (
        <section
            ref={sectionRef}
            className={`section_cta bg-[var(--primary--beige)] dark:bg-[var(--primary--dark)] ${className}`}
            aria-labelledby="final-cta-heading"
        >
            <div className="padding-global">
                <div className="container-large">
                    <div className="padding-section-large">
                        <div className="cta_component">
                            <div className="max-width-large align-center">
                                <div className="margin-bottom margin-small">
                                    <h2
                                        id="final-cta-heading"
                                        className="heading-style-h2 text-[var(--primary--burgundy)] dark:text-[var(--primary--pink)] text-center"
                                    >
                                        {headline}
                                    </h2>
                                </div>
                                <div className="margin-bottom margin-medium">
                                    <p className="text-size-medium text-center max-width-large">
                                        {subheading}
                                    </p>
                                </div>
                                <div className="button-row justify-center">
                                    <Link
                                        href={primaryCTA.href}
                                        className="button w-button"
                                        style={{
                                            backgroundColor: 'var(--primary--burgundy)',
                                            color: 'white',
                                            fontWeight: 600,
                                            transition: 'all 0.25s ease-in-out'
                                        }}
                                        onClick={handlePrimaryCTAClick}
                                        aria-label={`${primaryCTA.text} - Schedule a consultation with our admissions experts`}
                                    >
                                        {primaryCTA.text}
                                    </Link>
                                    <Link
                                        href={secondaryCTA.href}
                                        className="button is-border w-button text-[var(--primary--burgundy)] dark:text-[var(--primary--pink)]"
                                        style={{
                                            fontWeight: 500,
                                            transition: 'all 0.25s ease-in-out'
                                        }}
                                        onClick={handleSecondaryCTAClick}
                                        aria-label={`${secondaryCTA.text} - Get a complimentary evaluation of your profile`}
                                    >
                                        {secondaryCTA.text}
                                    </Link>
                                </div>
                                <div className="margin-top margin-small text-center">
                                    <p className="text-size-small text-color-gray">
                                        <strong>No ghostwriting.</strong> No generic templates. Just authentic, strategic guidance from alumni who&apos;ve been there.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// Export memoized component
export const FinalCTA = memo(FinalCTAComponent);
FinalCTA.displayName = 'FinalCTA';

// Default export for backward compatibility
export default FinalCTA;
